import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { labelSlug } from "./utils";

/**
 * Floating menu that appears next to a collapsed-rail group icon on hover.
 *
 * Rendered via a portal into `document.body` with `position: fixed` at the
 * trigger's viewport rect. Portalling is essential because the sidebar's
 * scrollable container has `overflow-y: auto`, which per CSS spec coerces
 * `overflow-x: visible` — a plain `absolute` flyout would be clipped at the
 * sidebar's right edge and hidden behind the main content.
 */
export default function SidebarGroupFlyout({ group, triggerRef, onLinkClick, onContextMenu, isPinned }) {
  const flyoutRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const el = triggerRef?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Anchor to top-right of the trigger button + 4px gap.
    setPos({ top: r.top, left: r.right + 4 });
  }, [triggerRef]);

  return createPortal(
    <div
      ref={flyoutRef}
      role="menu"
      data-inverse="flyout"
      data-testid={`sidebar-flyout-${group.key}`}
      style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 60 }}
      className="min-w-[220px] rounded-lg border border-white/10 bg-[#0F1420]/98 backdrop-blur-xl shadow-2xl py-2"
    >
      <div className="px-3 pb-2 mb-1 border-b border-white/[0.06] text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
        <group.icon className={`w-3.5 h-3.5 ${group.color}`} />
        {group.label}
      </div>
      {group.children.map((c) => {
        if (c.type === "subgroup") {
          return (
            <div key={c.key}>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <c.icon className={`w-3 h-3 ${c.color || "text-slate-500"}`} />
                {c.label}
              </div>
              {c.children.map((gc) => (
                <FlyoutLeaf
                  key={gc.to}
                  item={gc}
                  parentTestId={`sidebar-flyout-${group.key}-sub-${c.key}-item`}
                  onLinkClick={onLinkClick}
                  onContextMenu={onContextMenu}
                  isPinned={isPinned}
                />
              ))}
            </div>
          );
        }
        return (
          <FlyoutLeaf
            key={c.to}
            item={c}
            parentTestId={`sidebar-flyout-${group.key}-item`}
            onLinkClick={onLinkClick}
            onContextMenu={onContextMenu}
            isPinned={isPinned}
          />
        );
      })}
    </div>,
    document.body,
  );
}

/** One leaf row inside a hover flyout. Disabled state = TBD chip, unclickable. */
function FlyoutLeaf({ item, parentTestId, onLinkClick, onContextMenu, isPinned }) {
  const Icon = item.icon;
  const testId = `${parentTestId}-${labelSlug(item.label)}`;
  if (item.disabled) {
    return (
      <div
        data-testid={testId}
        aria-disabled="true"
        className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-600 cursor-not-allowed"
      >
        <Icon className="w-4 h-4 text-slate-600" />
        <span className="truncate">{item.label}</span>
        <span className="ml-auto text-[8.5px] font-mono uppercase tracking-widest px-1 py-0.5 rounded bg-white/[0.03] text-slate-500 border border-white/[0.06]">
          TBD
        </span>
      </div>
    );
  }
  return (
    <Link
      to={item.to}
      data-testid={testId}
      onClick={onLinkClick}
      onContextMenu={(e) => onContextMenu(e, item.to)}
      className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
    >
      <Icon className={`w-4 h-4 ${item.color}`} />
      <span className="truncate">{item.label}</span>
      {isPinned(item.to) && <Star className="w-3 h-3 ml-auto text-orange-400 fill-orange-400/40" />}
    </Link>
  );
}
